import React, {useEffect, useState, useCallback} from "react";
import {
  Container, Row, Col, Nav, NavItem, NavLink, Card, CardBody, CardTitle,
  Spinner, Button, UncontrolledTooltip
} from "reactstrap";
import classnames            from "classnames";
import Breadcrumbs           from "../../components/Common/Breadcrumb";
import { withTranslation }   from "react-i18next";
import { getKPIs, getInsights } from "../../services/kpiService";
import FiscalInformationBanner from "../../components/FiscalInformation/FiscalInformationBanner";

const KPIBox = ({id, label, value, subTitleValue, help}) => (
  <Col md="3" sm="6" className="mb-3 d-flex">
    <Card
      id={id}
      className="text-center shadow-none border border-1 border-secondary border-opacity-25 flex-fill"
      style={{ minHeight: "110px" }}
    >
      <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-3">

        <CardTitle
          tag="h6"
          className="text-secondary text-opacity-75 font-size-12 mb-2"
        >
          {label} <i className="mdi mdi-information text-primary" />
        </CardTitle>

        <div className="d-flex justify-content-center align-items-end gap-3">
          {Array.isArray(value) ? (
            value.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="fw-bold font-size-18">
                  {item.value} {subTitleValue && <span className="font-size-10">{subTitleValue}</span>}
                </div>
                <small className="text-muted">{item.label}</small>
              </div>
            ))
          ) : (
            <div className="fw-bold font-size-22">
              {value} {subTitleValue && <span className="font-size-10">{subTitleValue}</span>}
            </div>
          )}
        </div>

      </CardBody>
    </Card>
    <UncontrolledTooltip placement="top" target={id}>
      {help}
    </UncontrolledTooltip>
  </Col>
);

const InsightBox = ({id, label, value, subtitle, help}) => (
  <Col md="3" sm="6" className="mb-4">
    <Card id={id} className="text-center shadow-none border border-1 border-secondary border-opacity-25">
      <CardBody>
        <CardTitle tag="h6" className="text-secondary text-opacity-75 font-size-13">
          {label} <i className="mdi mdi-information text-primary" />
        </CardTitle>
        <h2 className="font-weight-bold mb-2">{value}</h2>
      </CardBody>
    </Card>
    <UncontrolledTooltip placement="top" target={id}>
      {help}
    </UncontrolledTooltip>
  </Col>
);

const Dashboard = ({t}) => {
  const [topTab,   setTopTab]   = useState("statistics");
  const [period,   setPeriod]   = useState("30d");
  const [loading,  setLoading]  = useState(false);

  const [globalKpis,     setGlobalKpis]     = useState({});
  const [kpis,     setKpis]     = useState({});
  const [insights, setInsights] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (topTab === "statistics") {
        const data = await getKPIs({ period });
        setKpis(data);
      } else {
        const data = await getInsights({ period });
        setInsights(data);
      }
    } catch {
    } finally { setLoading(false); }
  }, [topTab, period]);

  const loadGlobalKpis = async () => {
    const data = await getKPIs('all');
    setGlobalKpis(data);
  }

  useEffect(() => {
    loadGlobalKpis();
  }, [])

  useEffect(() => {
    loadData();
  },[loadData]);

  const PeriodNav = () => (
    <Nav pills className="border-bottom border-1 border-secondary border-opacity-10 mb-3">
      {[
        { id: "30d", lbl: "Derniers 30 jours" },
        { id: "12w", lbl: "Dernièrs 3 mois" },
        { id: "12m", lbl: "Derniers 12 mois" }
      ].map(p => {
        const isActive = period === p.id;
        return (
          <NavItem key={p.id}>
            <NavLink
              href="#"
              className={classnames({"fw-bold border-bottom border-primary rounded-0 border-2 text-black": isActive })}
              onClick={() => setPeriod(p.id)}
            >
              {p.lbl}
            </NavLink>
          </NavItem>
        );
      })}
    </Nav>
  );

  const getSubtitle = ({ type, value }) => {
    if (type === "rating") {
      if (value >= 4.5) return "Excellent";
      if (value >= 4.0) return "Très bien";
      if (value >= 3.0) return "Correct";
      return "À améliorer";
    }

    if (value <= 1)  return "Excellent";
    if (value <= 3)  return "Bien";
    if (value <= 5)  return "À surveiller";
    return "Critique";
  };


  return (
    <div className="page-content">
      <Container fluid>
        <FiscalInformationBanner />
        <Breadcrumbs
          title={t("Tableau de bord")}
          breadcrumbItem={t("Paniers - Gestion et Optimisation")}
        />

        <p>
          Ce tableau de bord vous permet de suivre <strong>vos performances</strong> :
          nombre de paniers sauvés, impact CO₂, popularité et impressions dans l’app.
          Les insights donnent un aperçu qualitatif (notes, annulations, remboursements).
        </p>

        {/* Onglets */}
        <Nav pills className="border-bottom border-1 border-secondary border-opacity-10">
          {["statistics", "insights"].map(k => {
            const isActive = topTab === k;
            return (
              <NavItem key={k}>
                <NavLink
                  href="#"
                  className={classnames({
                    "fw-bold border-bottom border-primary rounded-0 border-2 text-dark": isActive
                  })}
                  onClick={() => setTopTab(k)}
                >
                  {k === "statistics" ? t("Statistiques") : t("Insights")}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        {/* STATISTIQUES – toutes périodes */}
        {topTab==="statistics" && (
          <>
            <h5 className="mt-4 mb-3">Statistiques globales</h5>
            <Row>
              <KPIBox id="kpiMealsAll"   label="Paniers sauvés"
                      value={globalKpis.bags}
                      help="Total de paniers réellement collectés depuis votre inscription." />
              <KPIBox id="kpiPriceMealsByMethod" label="Revenus par mode de paiement"
                value={[
                  { label: 'Total', value: globalKpis.price },
                  { label: 'Espèces', value: globalKpis.totalPriceCash },
                  { label: 'CB', value: globalKpis.totalPriceCard }
                ]}
                subTitleValue="Dhs"
                help="Répartition des revenus selon le mode de paiement."
              />
              <KPIBox id="kpiCo2All"     label="Tonnes de CO₂ évitées"
                      value={globalKpis.co2}
                      help="Équivalent CO₂ économisé grâce aux paniers non gaspillés." />
              <KPIBox id="kpiFavsAll"    label="Favoris"
                      value={globalKpis.favs}
                      help="Nombre d’utilisateurs ayant mis votre boutique en favori." />
              <KPIBox id="kpiImprAll"    label="Impressions"
                      value={globalKpis.impressions}
                      help="Nombre d’affichages de vos paniers dans l’application." />
            </Row>
          </>
        )}

        {/* Sélecteur de période */}
        <h5 className="mt-4 mb-3">
          {topTab==="statistics" ? "Statistiques de la période" : "Insights"}
        </h5>
        <PeriodNav/>

        {loading ? (
          <div className="text-center my-5"><Spinner color="primary" /></div>
        ) : topTab==="statistics" ? (
          <Row>
            <KPIBox id="kpiMealsP"   label="Paniers sauvés"
                    value={kpis.bags}
                    help="paniers collectés sur la période sélectionnée." />
            <KPIBox id="kpiPriceMealsP" label="Revenus par mode de paiement"
                    value={[
                      { label: 'Total', value: kpis.price },
                      { label: 'Espèces', value: kpis.totalPriceCash },
                      { label: 'CB', value: kpis.totalPriceCard }
                    ]}
                    subTitleValue="Dhs"
                    help="Total des revenus générés sur la période sélectionnée."
            />
            <KPIBox id="kpiCo2P"     label="Tonnes de CO₂ évitées"
                    value={kpis.co2}
                    help="CO₂ économisé sur la période." />
            <KPIBox id="kpiFavsP"    label="Favoris"
                    value={kpis.favs}
                    help="Nouveaux utilisateurs ayant ajouté votre magasin en favori." />
            <KPIBox id="kpiImprP"    label="Impressions"
                    value={kpis.impressions}
                    help="Affichages de vos paniers sur la période." />
          </Row>
        ) : (
          <Row>
            <InsightBox
              id="insightRat"
              label="Note globale"
              value={`${insights.rating}★`}
              subtitle={getSubtitle({ type: "rating", value: insights.rating })}
              help="Moyenne de toutes vos notes sur la période."
            />
            <InsightBox
              id="insightBag"
              label="Note panier"
              value={`${insights.bagRating}★`}
              subtitle={getSubtitle({ type: "rating", value: insights.bagRating })}
              help="Note moyenne attribuée aux paniers."
            />
            <InsightBox
              id="insightStore"
              label="Note expérience magasin"
              value={`${insights.storeRating}★`}
              subtitle={getSubtitle({ type: "rating", value: insights.storeRating })}
              help="Note sur l’accueil et le service."
            />
            <InsightBox
              id="insightCancel"
              label="Annulations"
              value={`${insights.cancellations}%`}
              subtitle={getSubtitle({ type: "cancel", value: insights.cancellations })}
              help="Taux d’annulations de commandes sur la période."
            />
          </Row>
        )}

        <div className="text-center my-4">
          <Button color="primary" className="px-5 btn btn-sm" onClick={() => {loadGlobalKpis(); loadData(); }}>Rafraîchir</Button>
        </div>
      </Container>
    </div>
  );
};

export default withTranslation()(Dashboard);
